import {
  collection,
  doc,
  addDoc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "../firebase";

function makeInviteCode() {
  return Math.random().toString(36).substring(2, 10).toUpperCase();
}

async function getDonorPublicName(uid) {
  try {
    const donorSnap = await getDoc(doc(db, "donors", uid));

    if (!donorSnap.exists()) {
      return {
        displayName: "",
        firstName: "",
        lastInitial: "",
      };
    }

    const donor = donorSnap.data();
    const firstName = donor.firstName || "";
    const lastInitial = donor.lastName?.charAt(0) || "";

    return {
      displayName: firstName
        ? `${firstName}${lastInitial ? ` ${lastInitial}.` : ""}`
        : "",
      firstName,
      lastInitial,
    };
  } catch (err) {
    console.warn("Could not load donor public name:", err);

    return {
      displayName: "",
      firstName: "",
      lastInitial: "",
    };
  }
}

export async function createImpactNetwork({
  user,
  name,
  description = "",
  focusAreas = [],
}) {
  if (!user?.uid) throw new Error("User must be logged in.");
  if (!name?.trim()) throw new Error("Network name is required.");

  const inviteCode = makeInviteCode();
  const publicName = await getDonorPublicName(user.uid);

  const networkRef = await addDoc(collection(db, "impactNetworks"), {
    name: name.trim(),
    description: description.trim(),
    focusAreas,
    visibility: "inviteOnly",
    inviteCode,
    createdBy: user.uid,
    createdByEmail: user.email || "",
    createdAt: serverTimestamp(),
    status: "active",

    memberCount: 1,
    activeCommitmentCount: 0,
    monthlySupport: 0,
    organizationsSupported: 0,
    peopleImpacted: 0,
  });

  await setDoc(doc(db, "impactNetworks", networkRef.id, "members", user.uid), {
    uid: user.uid,
    email: user.email || "",
    displayName: publicName.displayName,
    firstName: publicName.firstName,
    lastInitial: publicName.lastInitial,
    role: "leader",
    status: "active",
    joinedAt: serverTimestamp(),
  });

  await setDoc(doc(db, "users", user.uid, "impactNetworks", networkRef.id), {
    networkId: networkRef.id,
    name: name.trim(),
    role: "leader",
    joinedAt: serverTimestamp(),
  });

  return {
    networkId: networkRef.id,
    inviteCode,
  };
}

export async function getNetworkByInviteCode(inviteCode) {
  const q = query(
    collection(db, "impactNetworks"),
    where("inviteCode", "==", inviteCode),
    where("status", "==", "active")
  );

  const snap = await getDocs(q);

  if (snap.empty) return null;

  const docSnap = snap.docs[0];

  return {
    id: docSnap.id,
    ...docSnap.data(),
  };
}

export async function joinImpactNetwork({ user, inviteCode }) {
  if (!user?.uid) throw new Error("User must be logged in.");
  if (!inviteCode) throw new Error("Invite code is required.");

  const network = await getNetworkByInviteCode(inviteCode);

  if (!network) {
    throw new Error("Impact Network not found.");
  }

  const memberRef = doc(db, "impactNetworks", network.id, "members", user.uid);
  const existing = await getDoc(memberRef);

  if (existing.exists()) {
    return {
      networkId: network.id,
      alreadyMember: true,
    };
  }

  const publicName = await getDonorPublicName(user.uid);

  await setDoc(memberRef, {
    uid: user.uid,
    email: user.email || "",
    displayName: publicName.displayName,
    firstName: publicName.firstName,
    lastInitial: publicName.lastInitial,
    role: "member",
    status: "active",
    joinedAt: serverTimestamp(),
  });

  await setDoc(doc(db, "users", user.uid, "impactNetworks", network.id), {
    networkId: network.id,
    name: network.name,
    role: "member",
    joinedAt: serverTimestamp(),
  });

  return {
    networkId: network.id,
    alreadyMember: false,
  };
}

export async function getMyImpactNetworks(userId) {
  const snap = await getDocs(collection(db, "users", userId, "impactNetworks"));

  return snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  }));
}

export async function getImpactNetwork(networkId) {
  const ref = doc(db, "impactNetworks", networkId);
  const snap = await getDoc(ref);

  if (!snap.exists()) return null;

  return {
    id: snap.id,
    ...snap.data(),
  };
}

export async function getImpactNetworkMembers(networkId) {
  const snap = await getDocs(
    query(
      collection(db, "impactNetworks", networkId, "members"),
      where("status", "==", "active")
    )
  );

  return snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  }));
}

export async function getImpactNetworkActivity(networkId) {
  const snap = await getDocs(
    query(
      collection(db, "impactNetworks", networkId, "activity"),
      orderBy("createdAt", "desc"),
      limit(10)
    )
  );

  return snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  }));
}